import { Platform } from '@angular/cdk/platform';
import { Inject, Injectable, Optional } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { DateTimeHourParts, LcsDateTimeAdapter } from './date-time-adapter';
import { ISO_8601_REGEX, range } from './date-utilities';

/** Adapts the native JS Date for use with cdk-based components that work with dates. */
@Injectable()
export class LcsNativeDateTimeAdapter extends LcsDateTimeAdapter<Date> {
   /** Whether to clamp the date between 1 and 9999 to avoid IE and Edge errors. */
   private readonly shouldClampDate: boolean;

   constructor(@Optional() @Inject(MAT_DATE_LOCALE) matDateLocale: string, platform: Platform) {
      super();
      super.setLocale(matDateLocale);

      this.shouldClampDate = platform.TRIDENT || platform.EDGE;
   }

   getYear(date: Date): number {
      return date.getFullYear();
   }

   getMonth(date: Date): number {
      return date.getMonth();
   }

   getDate(date: Date): number {
      return date.getDate();
   }

   getDayOfWeek(date: Date): number {
      return date.getDay();
   }

   getHours(date: Date): number {
      return date.getHours();
   }

   getMinutes(date: Date): number {
      return date.getMinutes();
   }

   getSeconds(date: Date): number {
      return date.getSeconds();
   }

   getDayStart(date: Date): Date {
      return this.createDateTimeWithOverflow(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
   }

   getDayEnd(date: Date): Date {
      return this.createDateTimeWithOverflow(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
   }

   getDayStartUtc(date: Date): Date {
      const newDate = this.getDayStart(date);
      newDate.setUTCHours(0, 0, 0, 0);
      return newDate;
   }

   getDayEndUtc(date: Date): Date {
      const newDate = this.getDayStart(date);
      newDate.setUTCHours(23, 59, 59, 999);
      return newDate;
   }

   getOpposingPeriod(date: Date, displayFormat: Intl.DateTimeFormatOptions): string {
      return (
         this.formatToParts(this.addHours(date, 12), displayFormat).find((part) => {
            return part.type === 'dayPeriod';
         })?.value ?? this.throwForDateTimePartNotFound('dayPeriod')
      );
   }

   formatPeriod(date: Date, displayFormat: Intl.DateTimeFormatOptions): string {
      return (
         this.formatToParts(date, displayFormat).find((part) => {
            return part.type === 'dayPeriod';
         })?.value ?? this.throwForDateTimePartNotFound('dayPeriod')
      );
   }

   throwForDateTimePartNotFound(partType: Intl.DateTimeFormatPartTypes): never {
      throw new Error(`Unable to find part type ${partType}`);
   }

   getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
      const dtf = new Intl.DateTimeFormat(this.locale, { month: style, timeZone: 'utc' });
      return range(12, (i) => {
         return this.stripDirectionalityCharacters(this._format(dtf, new Date(2017, i, 1)));
      });
   }

   getDateNames(): string[] {
      const dtf = new Intl.DateTimeFormat(this.locale, { day: 'numeric', timeZone: 'utc' });
      return range(31, (i) => {
         return this.stripDirectionalityCharacters(this._format(dtf, new Date(2017, 0, i + 1)));
      });
   }

   getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
      const dtf = new Intl.DateTimeFormat(this.locale, { timeZone: 'utc', weekday: style });
      return range(7, (i) => {
         return this.stripDirectionalityCharacters(this._format(dtf, new Date(2017, 0, i + 1)));
      });
   }

   getYearName(date: Date): string {
      const dtf = new Intl.DateTimeFormat(this.locale, { timeZone: 'utc', year: 'numeric' });
      return this.stripDirectionalityCharacters(this._format(dtf, date));
   }

   getFirstDayOfWeek(): number {
      // We can't tell using native JS Date what the first day of the week is, we default to Sunday.
      return 0;
   }

   getNumDaysInMonth(date: Date): number {
      return this.getDate(this.createDateTimeWithOverflow(this.getYear(date), this.getMonth(date) + 1, 0, 0, 0, 0));
   }

   clone(date: Date): Date {
      return this.createDateTime(
         this.getYear(date),
         this.getMonth(date),
         this.getDate(date),
         this.getHours(date),
         this.getMinutes(date),
         this.getSeconds(date)
      );
   }

   createDateTime(year: number, month: number, date: number, hours: number, minutes: number, seconds: number): Date {
      // Check for invalid month and date (except upper bound on date which we have to check after
      // creating the Date).
      if (month < 0 || month > 11) {
         throw Error(`Invalid month index "${month}". Month index has to be between 0 and 11.`);
      }

      if (date < 1) {
         throw Error(`Invalid date "${date}". Date has to be greater than 0.`);
      }

      const result = this.createDateTimeWithOverflow(year, month, date, hours, minutes, seconds);
      // Check that the date wasn't above the upper bound for the month, causing the month to overflow
      if (result.getMonth() !== month) {
         throw Error(`Invalid date "${date}" for month with index "${month}".`);
      }

      return result;
   }

   createDate(year: number, month: number, date: number): Date {
      return this.createDateTime(year, month, date, 0, 0, 0);
   }

   today(): Date {
      return new Date();
   }

   parse(value: any): Date | null {
      if (typeof value === 'number') {
         return new Date(value);
      }

      if (typeof value === 'string') {
         value = this.addColonToTime(value);
      }

      return value ? new Date(Date.parse(value)) : null;
   }

   format(date: Date, displayFormat: object): string {
      date = this.prepareForFormatting(date);
      const dtf = this.getDateTimeFormat(displayFormat);
      return this.stripDirectionalityCharacters(this._format(dtf, date));
   }

   // this is only necessary because new Intl.DateTimeFormat([], { minute: '2-digit' }).format(date)
   // does not pad the minutes digit, so it's effectively the same as { minute: 'numeric' }
   formatMinutes(date: Date, displayFormat: any): string {
      if (displayFormat.minute === '2-digit') {
         displayFormat = { ...displayFormat, hour: 'numeric', hour12: false };
      }

      const parts = this.formatToParts(date, displayFormat);
      const minutes: string | undefined = parts.find((part) => {
         return part.type === 'minute';
      })?.value;

      if (!minutes) {
         this.throwForDateTimePartNotFound('minute');
      }

      return minutes;
   }

   formatHours(date: Date, displayFormat: Intl.DateTimeFormatOptions): DateTimeHourParts {
      const parts = this.formatToParts(date, displayFormat);
      let dayPeriod: string | undefined;
      let hours: string | undefined;

      for (const part of parts) {
         if (part.type === 'dayPeriod') {
            dayPeriod = part.value;
         } else if (part.type === 'hour') {
            hours = part.value;
         }
      }

      if (!dayPeriod) {
         this.throwForDateTimePartNotFound('dayPeriod');
      }
      if (!hours) {
         this.throwForDateTimePartNotFound('hour');
      }

      return {
         dayPeriod,
         hours,
      };
   }

   formatDateTime(date: Date, displayFormat: object): string {
      if (Array.isArray(displayFormat)) {
         return displayFormat
            .map((format) => {
               return this.format(date, format);
            })
            .join(' ');
      }

      return this.format(date, displayFormat);
   }

   addCalendarYears(date: Date, years: number): Date {
      return this.addCalendarMonths(date, years * 12);
   }

   addCalendarMonths(dateTime: Date, months: number): Date {
      let newDate = this.createDateTimeWithOverflow(
         this.getYear(dateTime),
         this.getMonth(dateTime) + months,
         this.getDate(dateTime),
         this.getHours(dateTime),
         this.getMinutes(dateTime),
         this.getSeconds(dateTime)
      );

      // It's possible to wind up in the wrong month if the original month has more days than the new
      // month. In this case we want to go to the last day of the desired month.
      // Note: the additional + 12 % 12 ensures we end up with a positive number, since JS % doesn't
      // guarantee this.
      if (this.getMonth(newDate) !== (((this.getMonth(dateTime) + months) % 12) + 12) % 12) {
         newDate = this.createDateTimeWithOverflow(
            this.getYear(newDate),
            this.getMonth(newDate),
            0,
            this.getHours(newDate),
            this.getMinutes(newDate),
            this.getSeconds(dateTime)
         );
      }

      return newDate;
   }

   addCalendarDays(dateTime: Date, days: number): Date {
      return this.createDateTimeWithOverflow(
         this.getYear(dateTime),
         this.getMonth(dateTime),
         this.getDate(dateTime) + days,
         this.getHours(dateTime),
         this.getMinutes(dateTime),
         this.getSeconds(dateTime)
      );
   }

   addHours(dateTime: Date, hours: number): Date {
      return this.createDateTimeWithOverflow(
         this.getYear(dateTime),
         this.getMonth(dateTime),
         this.getDate(dateTime),
         this.getHours(dateTime) + hours,
         this.getMinutes(dateTime),
         this.getSeconds(dateTime)
      );
   }

   addMinutes(dateTime: Date, minutes: number): Date {
      return this.createDateTimeWithOverflow(
         this.getYear(dateTime),
         this.getMonth(dateTime),
         this.getDate(dateTime),
         this.getHours(dateTime),
         this.getMinutes(dateTime) + minutes,
         this.getSeconds(dateTime)
      );
   }

   toIso8601(date: Date): string {
      return [date.getUTCFullYear(), this._2digit(date.getUTCMonth() + 1), this._2digit(date.getUTCDate())].join('-');
   }

   /**
    * Returns the given value if given a valid Date or null. Deserializes valid ISO 8601 strings
    * (https://www.ietf.org/rfc/rfc3339.txt) into valid Dates and empty string into null. Returns an
    * invalid date for all other values.
    */
   deserialize(value: any): Date | null {
      if (typeof value === 'string') {
         if (!value) {
            return null;
         }
         // The `Date` constructor accepts formats other than ISO 8601, so we need to make sure the
         // string is the right format first.
         if (ISO_8601_REGEX.test(value)) {
            const dateOnly = this.deserializeDateOnlyIso8601(value);
            if (dateOnly && this.isValid(dateOnly)) {
               return dateOnly;
            }
            const date = new Date(value);
            if (this.isValid(date)) {
               return date;
            }
         }
      }
      return super.deserialize(value);
   }

   isDateInstance(obj: any) {
      return obj instanceof Date;
   }

   isValid(date: Date) {
      return !isNaN(date.getTime());
   }

   invalid(): Date {
      return new Date(NaN);
   }

   /**
    * Compares two datetimes.
    * @param first The first datetime to compare.
    * @param second The second datetime to compare.
    * @returns 0 if the datetimes are equal, a number less than 0 if the first datetime is earlier,
    *     a number greater than 0 if the first datetime is later.
    */
   compareDate(first: Date, second: Date): number {
      return (
         this.getYear(first) - this.getYear(second) ||
         this.getMonth(first) - this.getMonth(second) ||
         this.getDate(first) - this.getDate(second) ||
         this.getHours(first) - this.getHours(second) ||
         this.getMinutes(first) - this.getMinutes(second)
      );
   }

   /**
    * Pads a number to make it two digits.
    * @param n The number to pad.
    * @returns The padded number.
    */
   _2digit(n: number): string {
      return ('00' + n).slice(-2);
   }

   /**
    * Inserts a colon ':' between the hour and minute of a date time string
    * @param value the date time string to modify
    */
   protected addColonToTime(value: string): string {
      // Most Date.parse implementations can't handle times without a colon in them,
      // and we want users to be able to type '05/05/2020 530' and parse that to '05/05/2020 05:30'
      const split = value.split(' ');
      if (split.length >= 2 && !split[1].includes(':')) {
         const timePart = split[1];
         const num: number = parseInt(timePart, 10);
         if (1 <= num && num <= 24) {
            return `${split[0]} ${num}:00`;
         } else if (100 <= num && num <= 959) {
            return `${split[0]} ${timePart[0]}:${timePart.slice(-2)}`;
         } else if (1000 <= num && num <= 2400) {
            return `${split[0]} ${timePart.slice(0, 2)}:${timePart.slice(-2)}`;
         }
      }
      return value;
   }

   private deserializeDateOnlyIso8601(value: string): Date | null {
      // Dates in the yyyy-MM-DD format will be parsed as UTC time,
      // which will be incorrectly formatted a day earlier. For example,
      // 2020-05-05 will be parsed as a UTC datetime equivalent to 2020-05-05T00:00:00Z.
      // When we format that date in local time in any time zone to the west of UTC
      // (i.e., any time zone with a negative UTC offset), it will roll the day back one
      // and appear as '2020-05-04' instead of '2020-05-05'. So, we create a Date
      // manually in this case to prevent that issue.
      if (!value.includes('T')) {
         const split = value.split('-');
         if (split.length === 3) {
            return new Date(+split[0], +split[1] - 1, +split[2]);
         }
      }
      return null;
   }

   /** Creates a date but allows the month and date to overflow. */
   private createDateTimeWithOverflow(
      year: number,
      month: number,
      date: number,
      hours: number,
      minutes: number,
      seconds: number,
      milliseconds = 0
   ) {
      const result = new Date(year, month, date, hours, minutes, seconds, milliseconds);

      // We need to correct for the fact that JS native Date treats years in range [0, 99] as
      // abbreviations for 19xx.
      if (year >= 0 && year < 100) {
         result.setFullYear(this.getYear(result) - 1900);
      }
      return result;
   }

   /**
    * Strip out unicode LTR and RTL characters. Edge and IE insert these into formatted dates while
    * other browsers do not. We remove them to make output consistent and because they interfere with
    * date parsing.
    * @param str The string to strip direction characters from.
    * @returns The stripped string.
    */
   private stripDirectionalityCharacters(str: string) {
      return str.replace(/[\u200e\u200f]/g, '');
   }

   /**
    * Asserts validity and clamps the date if necessary
    * @param date Date to prepare for formatting
    */
   private prepareForFormatting(date: Date): Date {
      if (!this.isValid(date)) {
         throw Error(`NativeDateTimeAdapter: Cannot format invalid date: ${date}`);
      }

      // On IE and Edge the i18n API will throw a hard error that can crash the entire app
      // if we attempt to format a date whose year is less than 1 or greater than 9999.
      if (this.shouldClampDate && (date.getFullYear() < 1 || date.getFullYear() > 9999)) {
         date = this.clone(date);
         date.setFullYear(Math.max(1, Math.min(9999, date.getFullYear())));
      }

      return date;
   }

   private getDateTimeFormat(displayFormat: object): Intl.DateTimeFormat {
      displayFormat = { ...displayFormat, timeZone: 'utc' };
      return new Intl.DateTimeFormat(this.locale, displayFormat);
   }

   private formatToParts(date: Date, displayFormat: Intl.DateTimeFormatOptions): Intl.DateTimeFormatPart[] {
      date = this.prepareForFormatting(date);
      date = this.convertToUtc(date);
      const dtf = this.getDateTimeFormat(displayFormat);
      return dtf.formatToParts(date);
   }

   /**
    * When converting Date object to string, javascript built-in functions may return incorrect
    * results because they apply their internal DST rules. The DST rules around the world change
    * very frequently and the current valid rule is not always valid in previous years.
    * We work around this problem building a new Date object which has its internal UTC
    * representation with the local date and time.
    * @param dtf Intl.DateTimeFormat object, containing the desired string format. It must have
    *    timeZone set to 'utc' to work fine.
    * @param date Date from which we want to get the string representation according to dtf
    * @returns A Date object with its UTC representation based on the passed in date info
    */
   private _format(dtf: Intl.DateTimeFormat, date: Date) {
      date = this.convertToUtc(date);
      return dtf.format(date);
   }

   private convertToUtc(date: Date): Date {
      return new Date(
         Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds(),
            date.getMilliseconds()
         )
      );
   }
}
